import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useShop, money, stockInfo } from "../lib/shop";
import { StockBadge } from "./StockBadge";

export const ProductCard = ({ product, testId }) => {
    const { addToBasket, settings } = useShop();
    const hasVariations = (product.variations || []).length > 0;
    const price = product.sale_price || product.price;
    const info = stockInfo(product, settings.low_stock_threshold);
    const canBuy = price != null && info.state !== "out";

    return (
        <article
            data-testid={testId}
            className="card-lift bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col"
        >
            <Link to={`/products/${product.id}`} className="block aspect-[4/3] overflow-hidden bg-slate-50 relative">
                <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
                {product.sale_price != null && product.price != null && product.sale_price < product.price && (
                    <span className="absolute top-3 left-3 rounded-full bg-[#40E0D0] text-[#0033A0] text-xs font-extrabold px-3 py-1" data-testid={`sale-badge-${product.id}`}>
                        Offer
                    </span>
                )}
            </Link>
            <div className="p-4 md:p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="inline-block rounded-full bg-[#40E0D0]/20 text-[#0033A0] text-xs font-bold px-3 py-1">
                        {product.category}
                    </span>
                    <StockBadge product={product} />
                </div>
                <Link to={`/products/${product.id}`}>
                    <h3 className="font-heading font-bold text-slate-900 text-sm md:text-lg hover:text-[#0033A0] transition-colors">
                        {product.name}
                    </h3>
                </Link>
                <p className="mt-1 text-xs md:text-sm text-slate-500 leading-relaxed flex-1">{product.description}</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                    {price != null ? (
                        <p className="font-heading font-extrabold text-[#0033A0] text-base md:text-xl" data-testid={`price-${product.id}`}>
                            {money(price)}
                            {product.sale_price != null && product.price != null && product.sale_price < product.price && (
                                <span className="ml-2 text-sm text-slate-400 line-through font-normal">{money(product.price)}</span>
                            )}
                            <span className="block text-xs font-normal text-slate-400">{hasVariations ? "from · see options" : product.unit !== "each" ? product.unit : ""}</span>
                        </p>
                    ) : (
                        <p className="text-xs md:text-sm text-slate-400 font-bold" data-testid={`price-unset-${product.id}`}>Price in store</p>
                    )}
                    {canBuy && !hasVariations && (
                        <button
                            type="button"
                            onClick={() => addToBasket(product)}
                            data-testid={`add-${product.id}`}
                            aria-label={`Add ${product.name} to basket`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#0033A0] text-white px-4 py-2 text-sm font-heading font-bold hover:bg-[#002277] transition-colors"
                        >
                            <Plus className="h-4 w-4" /> Add
                        </button>
                    )}
                    {hasVariations && (
                        <Link
                            to={`/products/${product.id}`}
                            data-testid={`options-${product.id}`}
                            className="inline-flex items-center gap-1 rounded-full border-2 border-[#0033A0] text-[#0033A0] px-4 py-1.5 text-sm font-heading font-bold hover:bg-slate-50 transition-colors"
                        >
                            Options
                        </Link>
                    )}
                </div>
            </div>
        </article>
    );
};
